import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LuTrash } from "react-icons/lu";
import { semesters as defaultSemesters } from "../courses";

const Semester = ({ title, gpa, onChange, onDelete }) => {
  const idBase = title.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="course-card">
      <div className="course-header">
        <span className="badge">{title}</span>
        <button type="button" className="icon-btn" onClick={onDelete} aria-label="Remove semester">
          <LuTrash className="icon" />
        </button>
      </div>
      <div className="fields">
        <div className="field">
          <label htmlFor={`${idBase}-gpa`}>Semester GPA</label>
          <input
            id={`${idBase}-gpa`}
            type="number"
            step="0.01"
            placeholder="GPA"
            value={gpa}
            onChange={onChange}
            min="0"
            max="10"
          />
          <div className="inline-hint">Enter GPA as provided by VIT results.</div>
        </div>
      </div>
    </div>
  );
};

const CGPAPredict = () => {
  const [semesters, setSemesters] = useState([]);

  const [cgpa, setCgpa] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [resultTone, setResultTone] = useState("success");
  const [resultNote, setResultNote] = useState("");

  useEffect(() => {
    const savedSemesters = JSON.parse(localStorage.getItem("semesters"));
    if (savedSemesters) setSemesters(savedSemesters);
    else setSemesters(defaultSemesters);
  }, []);

  const handleSave = () => {
    localStorage.setItem("semesters", JSON.stringify(semesters));
    setFeedback({ type: "success", message: "Semesters saved locally for quick recall." });
  };

  const handleInputChange = (index, event) => {
    const { value } = event.target;
    setSemesters((prevSemesters) => {
      const updatedSemesters = [...prevSemesters];
      updatedSemesters[index].gpa = value;
      return updatedSemesters;
    });
  };

  const handleAddSemester = () => {
    setSemesters((prevSemesters) => [
      ...prevSemesters,
      {
        title: `Semester ${prevSemesters.length + 1}`,
        gpa: "",
      },
    ]);
  };

  const handleDeleteSemester = (index) => {
    setSemesters((prevSemesters) => prevSemesters.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    setSemesters((prevSemesters) =>
      prevSemesters.map((semester) => ({
        ...semester,
        gpa: "",
      }))
    );
  };

  const handleCalculateCGPA = () => {
    setIsCalculating(true);
    const totalGpa = semesters.reduce(
      (sum, semester) => sum + (parseFloat(semester.gpa) || 0),
      0
    );
    const count = semesters.filter((semester) => parseFloat(semester.gpa)).length;
    if (count === 0) {
      setCgpa(0);
      setResultTone("warning");
      setResultNote("Add semester GPAs to see your cumulative average.");
    } else {
      const calculated = (totalGpa / count).toFixed(2);
      setCgpa(calculated);

      const value = parseFloat(calculated);
      if (value >= 9) {
        setResultTone("success");
        setResultNote("Top-tier CGPA - great for dream/TT and scholarships.");
      } else if (value >= 8) {
        setResultTone("warning");
        setResultNote("Healthy - push big-credit courses next sem to reach 9.");
      } else if (value >= 7) {
        setResultTone("warning");
        setResultNote("Stable - avoid low grades in heavy slots to hold ground.");
      } else {
        setResultTone("danger");
        setResultNote("Caution - improve in core/elective heavies to stay drive-eligible.");
      }
    }

    setTimeout(() => setIsCalculating(false), 160);
  };

  const hasInvalidInputs = semesters.some(
    (semester) =>
      (semester.gpa && parseFloat(semester.gpa) < 0) ||
      (semester.gpa && parseFloat(semester.gpa) > 10)
  );

  const primaryLabel = isCalculating ? "Calculating..." : "Calculate CGPA";

  return (
    <div className="app-shell">
      <main className="page">
        <div className="nav-actions">
          <Link to="/" className="btn btn-ghost">← Home</Link>
          <span className="pill">CGPA Calculator</span>
        </div>

        <header className="page-header">
          <p className="eyebrow">Progress</p>
          <h1 className="page-title">Track cumulative GPA</h1>
          <p className="lede">Record each semester GPA and view your overall CGPA instantly.</p>
        </header>

        <div className="card">
          <div className="card-heading">
            <div>
              <h2 className="card-title">Semesters</h2>
              <p className="section-note">Add as many semesters as you need and update GPAs anytime.</p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleAddSemester}>
              + Add semester
            </button>
          </div>

          <div className="stack">
            {semesters.map((semester, index) => (
              <Semester
                key={index}
                title={semester.title}
                gpa={semester.gpa}
                onChange={(e) => handleInputChange(index, e)}
                onDelete={() => handleDeleteSemester(index)}
              />
            ))}
          </div>

          <div className="controls">
            <button type="button" className="btn btn-secondary" onClick={handleSave}>
              Save
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCalculateCGPA}
              disabled={hasInvalidInputs || isCalculating}
            >
              {primaryLabel}
            </button>
          </div>
          <div className="inline-hint">Values stay in your browser; no data leaves your device.</div>
          {feedback && <div className={`feedback ${feedback.type}`} aria-live="polite">{feedback.message}</div>}
        </div>

        {cgpa !== null && (
          <div className={`result-card ${resultTone}`}>
            <p className="result-label">Cumulative GPA</p>
            <p className="result-value">{cgpa}</p>
            <p className="muted">Average of provided semester GPAs.</p>
            {resultNote && <p className="muted" style={{ marginTop: 6 }}>{resultNote}</p>}
          </div>
        )}
      </main>
    </div>
  );
};

export default CGPAPredict;
